import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { isGoodRunStatus } from '@infras/shared-types';

/**
 * Scanner freshness — the one signal that decides whether anything else on
 * screen can be trusted. ONE hook so the top-bar pill, the sidebar Scheduler
 * dot and the overview cards can never disagree (they did when each read its
 * own query).
 *
 * Reads the SAME `jobDefinitions` query `SchedulerPage` uses; there is no
 * second trigger path and no new resolver.
 *
 * WHY the staleness threshold is derived per job rather than a constant: the
 * cadences span 1 minute (agent-command-reaper) to 12 hours (secret-inventory),
 * so any fixed cutoff is wrong at one end. A job is late at **2× its own
 * cadence** — which for the 1-minute reaper means "the scheduler is stuck" and
 * for the 12-hourly secret scan means "a whole cycle was missed". Both are
 * worth amber, and the rule needs no maintenance when a job is added.
 *
 * Equally deliberate: there is NO curated list of "real" scanners. Every
 * enabled job is measured. A hand-kept allowlist would silently stop covering
 * whatever job someone adds next.
 */
const JOBS = gql`
  query FreshnessJobs {
    jobDefinitions {
      name
      scheduleCron
      enabled
      lastRun {
        status
        startedAt
        finishedAt
        stats
      }
    }
  }
`;

interface JobRow {
  name: string;
  scheduleCron?: string | null;
  enabled: boolean;
  lastRun?: {
    status: string;
    startedAt: string;
    finishedAt?: string | null;
    /** JSON string — shape is per-job, so callers must treat it as unknown. */
    stats?: string | null;
  } | null;
}

export type FreshnessState = 'fresh' | 'late' | 'failed' | 'never' | 'paused';

export interface ScannerFreshness {
  name: string;
  cron?: string | null;
  /** Expected gap between runs, from the cron. undefined = couldn't parse. */
  intervalMs?: number;
  status?: string;
  startedAt?: string;
  /** Age of the last run's start. */
  ageMs?: number;
  /**
   * Time until the next run is due. NEGATIVE when the run is already overdue —
   * deliberately not floored at 0, because flooring made an overdue job read
   * "next in just now", which is the opposite of the truth.
   */
  nextInMs?: number;
  state: FreshnessState;
  /** Raw per-job stats JSON from the last run — see `statsSummary`. */
  stats?: string | null;
}

/**
 * One-line summary of a run's stats. The shape is per-job and untyped (a JSON
 * string column), so this stays defensive: pick the numeric entries, show the
 * few that are non-zero, and render nothing at all rather than guessing when the
 * payload isn't an object of numbers.
 */
export function statsSummary(stats?: string | null, max = 3): string | null {
  if (!stats) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(stats);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const nums = Object.entries(parsed as Record<string, unknown>)
    .filter((e): e is [string, number] => typeof e[1] === 'number' && e[1] !== 0)
    .slice(0, max);
  return nums.length ? nums.map(([k, v]) => `${k} ${v}`).join(' · ') : null;
}

/**
 * Expected interval for the 5-field crons this app actually uses. Handles the
 * four shapes present in `apps/scheduler/src/jobs/`, written here with STAR for
 * the asterisk so the examples don't close this comment:
 *   `STAR/15 * * * *`   → every 15 min          (atlas-whitelist-reconcile)
 *   `0,30 * * * *`      → a minute LIST, 60/count (gitlab-scan → 30 min)
 *   `15 STAR/12 * * *`  → every 12 h            (secret-inventory)
 *   `15 3 * * *`        → daily
 * Anything else returns undefined and the job is reported without a threshold
 * rather than guessed at.
 */
export function cronIntervalMs(cron?: string | null): number | undefined {
  if (!cron) return undefined;
  const f = cron.trim().split(/\s+/);
  if (f.length !== 5) return undefined;
  const [min, hour] = f;
  const MIN = 60_000;

  const stepMin = /^\*\/(\d+)$/.exec(min);
  if (stepMin) return Number(stepMin[1]) * MIN;

  // minute list / single minute → the hour field decides the outer period
  const minCount = /^\d+(,\d+)*$/.test(min) ? min.split(',').length : undefined;
  if (minCount) {
    if (hour === '*') return (60 / minCount) * MIN;
    const stepHour = /^\*\/(\d+)$/.exec(hour);
    if (stepHour) return (Number(stepHour[1]) * 60 * MIN) / minCount;
    if (/^\d+(,\d+)*$/.test(hour)) return (24 * 60 * MIN) / (minCount * hour.split(',').length);
  }
  return undefined;
}

function classify(j: JobRow, now: number): ScannerFreshness {
  const intervalMs = cronIntervalMs(j.scheduleCron);
  const startedAt = j.lastRun?.startedAt;
  const ageMs = startedAt ? now - new Date(startedAt).getTime() : undefined;
  const base = {
    name: j.name,
    cron: j.scheduleCron,
    intervalMs,
    status: j.lastRun?.status,
    startedAt,
    ageMs,
    stats: j.lastRun?.stats,
  };
  const nextInMs = intervalMs !== undefined && ageMs !== undefined ? intervalMs - ageMs : undefined;

  if (!j.enabled) return { ...base, nextInMs, state: 'paused' };
  if (!j.lastRun) return { ...base, nextInMs, state: 'never' };
  // Shared predicate, not a literal — see isGoodRunStatus for why comparing
  // against 'success' (the Prometheus spelling) is a trap.
  if (!isGoodRunStatus(j.lastRun.status)) {
    return { ...base, nextInMs, state: 'failed' };
  }
  if (intervalMs !== undefined && ageMs !== undefined && ageMs > intervalMs * 2) {
    return { ...base, nextInMs, state: 'late' };
  }
  return { ...base, nextInMs, state: 'fresh' };
}

/** Worst first — what the pill should show. `paused` is not a problem. */
const SEVERITY: Record<FreshnessState, number> = { failed: 0, never: 1, late: 2, fresh: 3, paused: 4 };

export function relTime(ms?: number): string {
  if (ms === undefined) return '—';
  const m = Math.round(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

export interface Freshness {
  loading: boolean;
  error?: Error;
  scanners: ScannerFreshness[];
  /** Worst non-paused scanner, or null when there is nothing to report. */
  worst: ScannerFreshness | null;
  allFresh: boolean;
  /** Pill copy: "all scanners fresh · gitlab-scan 14m ago" / "secret-inventory late · 2d". */
  label: string;
  tone: 'ok' | 'warn';
}

/**
 * @param skip pass true for a role that cannot see inventory — a new_member must
 *   not fire this query at all (the resolver is role-blocked and it would only
 *   produce a console error behind chrome they can't act on).
 */
export function useFreshness(skip = false): Freshness {
  const { data, loading, error } = useQuery<{ jobDefinitions: JobRow[] }>(JOBS, {
    skip,
    pollInterval: 60_000,
    fetchPolicy: 'cache-and-network',
  });

  const now = Date.now();
  const scanners = (data?.jobDefinitions ?? [])
    .map((j) => classify(j, now))
    .sort((a, b) => SEVERITY[a.state] - SEVERITY[b.state] || a.name.localeCompare(b.name));

  const live = scanners.filter((s) => s.state !== 'paused');
  const worst = live[0] ?? null;
  const allFresh = live.length > 0 && live.every((s) => s.state === 'fresh');

  let label = 'scanner status unknown';
  if (loading && !data) label = 'checking scanners…';
  else if (error) label = 'scanner status unavailable';
  else if (allFresh) {
    const newest = [...live].sort((a, b) => (a.ageMs ?? Infinity) - (b.ageMs ?? Infinity))[0];
    label = `all scanners fresh · ${newest.name} ${relTime(newest.ageMs)} ago`;
  } else if (worst) {
    const what = worst.state === 'never' ? 'never run' : worst.state;
    label = `${worst.name} ${what}${worst.ageMs !== undefined ? ` · ${relTime(worst.ageMs)}` : ''}`;
  }

  return {
    loading,
    error: error ?? undefined,
    scanners,
    worst,
    allFresh,
    label,
    tone: allFresh || (loading && !data) ? 'ok' : 'warn',
  };
}
